import { Intervention, ScenarioWithInventory } from '@/types';
import { useCallback, useState } from 'react';
import { useInterventionKey } from '@/hooks/use-intervention-key';
import { Link } from 'react-router-dom';
import { noop, isEmpty } from 'lodash/fp';
import {
  Anchor,
  Text,
  Stack,
  LabelText,
  Secondary,
  Inline,
  InlineItem,
  UtilityButton,
  Icon,
} from '@kinesis/bungle';
import { Detail } from './detail';
import { CopyIntervention } from '@/components/copy-intervention';
import { contextColors } from '@/data/colors';
import { helpFor } from '@/data/help';

const SidebarIntervention = (props: SidebarInterventionProps) => {
  const {
    inventoryId,
    scenarioId,
    intervention,
    onUpdateIntervention = noop,
    enableCopy = false,
    scenarios = [],
    onCopyIntervention = async () => {},
  } = props;
  const interventionKey = useInterventionKey();
  const [showCopy, setShowCopy] = useState(false);
  const openCopy = useCallback(() => setShowCopy(true), []);
  const closeCopy = useCallback(() => setShowCopy(false), []);
  const onUpdate = useCallback(
    (f: (x: Intervention) => Intervention) => {
      return onUpdateIntervention(intervention.id, f);
    },
    [intervention.id, onUpdateIntervention],
  );
  return (
    <Stack space='medium'>
      <Stack space='xsmall'>
        <Stack space='none'>
          <Inline space='small'>
            <InlineItem sizing='fit-content'>
              <Icon
                color={contextColors[intervention.tag]}
                magnitude='xsmall'
                type='circle'
              />
            </InlineItem>
            <InlineItem sizing='fill-container'>
              <LabelText as='label'>{intervention.name}</LabelText>
            </InlineItem>
            <InlineItem sizing='fit-content'>
              <Inline space='xsmall'>
                <InlineItem sizing='fit-content'>
                  <UtilityButton
                    icon='bulb'
                    magnitude='small'
                    as={Anchor}
                    variant='default'
                    href={helpFor(intervention)}
                    target='_blank'
                  />
                </InlineItem>

                {enableCopy && (
                  <InlineItem sizing='fit-content'>
                    <UtilityButton
                      icon='copy'
                      magnitude='small'
                      variant='default'
                      onClick={openCopy}
                    />
                  </InlineItem>
                )}

                <InlineItem sizing='fit-content'>
                  <UtilityButton
                    icon='control'
                    magnitude='small'
                    as={Link}
                    variant={
                      interventionKey == intervention.id ? 'accent' : 'default'
                    }
                    to={
                      interventionKey == intervention.id
                        ? `/communities/${inventoryId}/pathways/${scenarioId}`
                        : `/communities/${inventoryId}/pathways/${scenarioId}/interventions/${intervention.id}`
                    }
                  />
                </InlineItem>
              </Inline>
            </InlineItem>
          </Inline>
          <LabelText as='label'>
            <Secondary>{intervention.description}</Secondary>
          </LabelText>
        </Stack>
        {!isEmpty(intervention.link) && (
          <Inline space='xsmall'>
            <InlineItem sizing='fit-content'>
              <Text tone='secondary'>Reference: </Text>
            </InlineItem>
            <InlineItem sizing='fill-container'>
              <Anchor href={intervention.link} target='_blank'>
                <Inline space='xsmall'>
                  <InlineItem ellipsify>{intervention.link}</InlineItem>
                  <InlineItem>
                    <Icon type='external-link' />
                  </InlineItem>
                </Inline>
              </Anchor>
            </InlineItem>
          </Inline>
        )}
      </Stack>
      <Detail intervention={intervention} onUpdate={onUpdate} />
      {showCopy && (
        <CopyIntervention
          intervention={intervention}
          onClose={closeCopy}
          scenarios={scenarios}
          onCopy={onCopyIntervention}
        />
      )}
    </Stack>
  );
};

export type SidebarInterventionProps = {
  inventoryId: number;
  scenarioId: number;
  intervention: Intervention;
  onUpdateIntervention?: (
    id: string | number,
    v: (x: Intervention) => Intervention,
  ) => void;
  enableCopy?: boolean;
  scenarios?: ScenarioWithInventory[];
  onCopyIntervention?: (
    scenarioId: number,
    intervention: Intervention,
  ) => Promise<void>;
};

export { SidebarIntervention };
